import { useState, useEffect } from 'react';
import { Bug, AlertTriangle, Search, Camera, BookOpen, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import EnhancedPestWeatherAlerts from '../components/EnhancedPestWeatherAlerts';

interface User {
  id: string;
  email?: string;
  user_metadata?: any;
}

interface PestControlProps {
  user: User | null;
  onSignIn?: () => void;
}

export default function PestControl({ user, onSignIn }: PestControlProps) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    'Pest Alerts',
    'Pest Identification', 
    'Treatment Guide',
    'AI Pest Advisor'
  ];

  const commonPests = [
    {
      name: 'Aphids',
      description: 'Small, soft-bodied insects that feed on plant sap',
      symptoms: 'Yellowing leaves, sticky honeydew, stunted growth',
      treatment: 'Insecticidal soap, neem oil, or beneficial insects'
    },
    {
      name: 'Caterpillars',
      description: 'Larvae of moths and butterflies that eat plant leaves',
      symptoms: 'Holes in leaves, visible caterpillars, droppings',
      treatment: 'Hand picking, Bt spray, or targeted insecticides'
    },
    {
      name: 'Spider Mites',
      description: 'Tiny arachnids that cause stippling on leaves',
      symptoms: 'Yellow stippling, fine webbing, leaf drop',
      treatment: 'Miticides, predatory mites, or strong water spray'
    },
    {
      name: 'Whiteflies',
      description: 'Small white flying insects that suck plant juices',
      symptoms: 'Yellowing leaves, sticky honeydew, flying insects',
      treatment: 'Yellow sticky traps, insecticidal soap, or systemic insecticides'
    }
  ];

  const handleAIChat = () => {
    // Navigate to AI chat with pest control context
    const aiPanel = document.querySelector('[data-ai-panel]');
    if (aiPanel) {
      aiPanel.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const textarea = document.querySelector('textarea[placeholder*="AgroSense AI"]') as HTMLTextAreaElement;
        if (textarea) {
          textarea.value = 'Help me identify and control pests in my crops. What should I look for?';
          textarea.focus();
        }
      }, 500);
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Bug className="w-16 h-16 text-red-600 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Pest Control Center</h2>
        <p className="text-gray-600 mb-6 max-w-md">
          Sign in to access personalized pest identification, treatment recommendations, and real-time alerts for your crops.
        </p>
        <button
          onClick={onSignIn}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Sign In to Continue
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Bug className="w-8 h-8 text-red-600" />
          Pest Control Center
        </h1>
        <p className="text-gray-600">
          Identify, monitor, and control pests to protect your crops
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === index
                  ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Pest Alerts Tab */}
          {activeTab === 0 && (
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Active Pest Alerts
              </h3>
              <EnhancedPestWeatherAlerts userId={user.id} />
            </div>
          )}

          {/* Pest Identification Tab */}
          {activeTab === 1 && (
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-red-600" />
                Pest Identification
              </h3>
              
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <Camera className="w-8 h-8 text-blue-600 mb-2" />
                  <h4 className="font-semibold mb-2">Photo Analysis</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Take a photo of affected plants for AI-powered pest identification
                  </p>
                  <button className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                    Analyze Photo
                  </button>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <BookOpen className="w-8 h-8 text-green-600 mb-2" />
                  <h4 className="font-semibold mb-2">Pest Library</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Browse our comprehensive database of common crop pests
                  </p>
                  <button className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors">
                    Browse Pests
                  </button>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <MessageCircle className="w-8 h-8 text-purple-600 mb-2" />
                  <h4 className="font-semibold mb-2">AI Assistant</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Describe symptoms and get instant pest identification help
                  </p>
                  <button 
                    onClick={handleAIChat}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  >
                    Ask AI
                  </button>
                </div>
              </div>

              <h4 className="text-lg font-semibold mb-3">Common Crop Pests</h4>
              <div className="grid gap-4 md:grid-cols-2">
                {commonPests.map((pest, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                    <h5 className="font-semibold text-red-700 mb-2">{pest.name}</h5>
                    <p className="text-sm text-gray-600 mb-2">{pest.description}</p>
                    <div className="space-y-1 text-sm">
                      <p><strong>Symptoms:</strong> {pest.symptoms}</p>
                      <p><strong>Treatment:</strong> {pest.treatment}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Treatment Guide Tab */}
          {activeTab === 2 && (
            <div>
              <h3 className="text-xl font-semibold mb-4">Treatment Guide</h3>
              
              <div className="space-y-6">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">Organic Methods</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Beneficial insects (ladybugs, lacewings)</li>
                    <li>• Neem oil and insecticidal soaps</li>
                    <li>• Companion planting</li>
                    <li>• Physical barriers and traps</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold text-yellow-800 mb-2">Chemical Controls</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Targeted insecticides (use as last resort)</li>
                    <li>• Systemic treatments for severe infestations</li>
                    <li>• Always follow label instructions</li>
                    <li>• Consider environmental impact</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">Prevention Tips</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Regular crop monitoring and inspection</li>
                    <li>• Proper plant spacing for air circulation</li>
                    <li>• Remove infected plant material promptly</li>
                    <li>• Rotate crops to break pest cycles</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* AI Pest Advisor Tab */}
          {activeTab === 3 && (
            <div>
              <h3 className="text-xl font-semibold mb-4">AI Pest Advisor</h3>
              <div className="bg-purple-50 p-6 rounded-lg border border-purple-200 text-center">
                <MessageCircle className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Get Expert Pest Advice</h4>
                <p className="text-gray-600 mb-4">
                  Describe your pest problems and get personalized treatment recommendations from our AI advisor.
                </p>
                <button 
                  onClick={handleAIChat}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Start AI Consultation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}